import { createContext, forwardRef, useCallback, useContext, useEffect, useMemo, type ComponentProps, type ReactNode } from 'react';
import * as DailyReact from '@daily-co/daily-react';
import type { NetworkThreshold } from './NetworkPanel';

/** Metadata and control only. Native video tracks never cross the JavaScript bridge. */
export type CallParticipant = {
  session_id: string; user_id?: string; user_name?: string; local?: boolean; owner?: boolean;
  audio?: boolean; video?: boolean; screen?: boolean; joined_at?: number | Date;
  userData?: unknown;
  permissions?: { canSend?: boolean | string[] | Set<string>; canAdmin?: boolean | string[] | Set<string> };
};
export type CallMessage<T = unknown> = { data?: T; fromId?: string };
export type ParticipantUpdate = { setAudio?: false; setVideo?: false; eject?: true };
export type CallTransport = {
  kind: 'native';
  /** Live state, including the interval before React observes a room transfer. */
  meetingState: () => string;
  participants: () => Record<string, CallParticipant>;
  sendAppMessage: (data: unknown, to?: string) => void;
  setUserData: (data: unknown) => void;
  setLocalAudio: (enabled: boolean) => void;
  setLocalVideo: (enabled: boolean) => void;
  updateParticipant: (id: string, changes: ParticipantUpdate) => void;
  updateParticipants: (changes: Record<string, ParticipantUpdate>) => void;
  startTranscription: () => void;
};
export type TransportEvent = { type: string; data?: unknown; fromId?: string; participant?: CallParticipant };
export type NativeTransportContextValue = {
  client: CallTransport;
  participants: Record<string, CallParticipant>;
  localSessionId: string;
  isTranscribing: boolean;
  isRecording?: boolean;
  activeSpeakerId?: string;
  /** Daily's native 0-100 network quality score. */
  networkQuality?: number;
  subscribe: (type: string, listener: (event: TransportEvent) => void) => () => void;
};
/**
 * Native shells draw participant video themselves. The web call components render a
 * transparent placeholder where each video belongs, and the shell positions its native
 * view at that rectangle (beneath the page on shells that support it).
 */
export type NativeVideoPlaceholderProps = { sessionId: string; type: 'video' | 'screenVideo'; fit: 'cover' | 'contain'; className?: string };
const NativeVideoPlaceholderContext = createContext<((props: NativeVideoPlaceholderProps) => ReactNode) | null>(null);
export const NativeVideoPlaceholderProvider = NativeVideoPlaceholderContext.Provider;
const NativeTransportContext = createContext<NativeTransportContextValue | null>(null);
export function NativeCallTransportProvider({ value, children }: { value: NativeTransportContextValue; children: ReactNode }) {
  return <NativeTransportContext.Provider value={value}>{children}</NativeTransportContext.Provider>;
}
export const useNativeCallTransport = () => useContext(NativeTransportContext);
const useNativeDaily = () => useContext(NativeTransportContext)!.client;
const useNativeLocalSessionId = () => useContext(NativeTransportContext)!.localSessionId;

// A call surface fixes its transport for its entire mounted lifetime. These dispatchers
// let shared tools use real Daily hooks on the web and native metadata on mobile without
// constructing a browser Daily client, tracks, or a second media connection.
export function useDaily() {
  const native = useNativeCallTransport();
  const useClient = native ? useNativeDaily : DailyReact.useDaily;
  return useClient();
}
export function useLocalSessionId() {
  const native = useNativeCallTransport();
  const useId = native ? useNativeLocalSessionId : DailyReact.useLocalSessionId;
  return useId();
}
function useNativeEvent(type: string, listener: (event: TransportEvent) => void) {
  const context = useContext(NativeTransportContext)!;
  const { subscribe } = context;
  useEffect(() => subscribe(type, listener), [subscribe, listener, type]);
}
export function useDailyEvent(type: Parameters<typeof DailyReact.useDailyEvent>[0], listener: (event: any) => void) {
  const native = useNativeCallTransport();
  const useEvent = native ? useNativeEvent : DailyReact.useDailyEvent;
  useEvent(type, listener);
}
function useNativeAppMessage<T>({ onAppMessage }: { onAppMessage?: (event: CallMessage<T>) => void } = {}) {
  const context = useContext(NativeTransportContext)!;
  const onMessage = useCallback((event: TransportEvent) => onAppMessage?.({ fromId: event.fromId, data: event.data as T }), [onAppMessage]);
  useNativeEvent('app-message', onMessage);
  return useCallback((data: T, to = '*') => context.client.sendAppMessage(data, to), [context.client]);
}
export function useAppMessage<T = unknown>(options: { onAppMessage?: (event: CallMessage<T>) => void } = {}) {
  const native = useNativeCallTransport();
  const useMessage = native ? useNativeAppMessage<T> : DailyReact.useAppMessage<T>;
  return useMessage(options);
}
type ParticipantOptions = { filter?: 'local' | 'remote' | ((participant: CallParticipant) => boolean); sort?: 'joined_at' | ((a: CallParticipant, b: CallParticipant) => -1 | 0 | 1) };
function useNativeParticipantIds(options: ParticipantOptions = {}) {
  const context = useContext(NativeTransportContext)!;
  return useMemo(() => {
    let participants = Object.values(context.participants);
    if (options.filter === 'local') participants = participants.filter(person => person.local);
    else if (options.filter === 'remote') participants = participants.filter(person => !person.local);
    else if (typeof options.filter === 'function') participants = participants.filter(options.filter);
    if (typeof options.sort === 'function') participants.sort(options.sort);
    else if (options.sort === 'joined_at') participants.sort((a, b) => Number(a.joined_at || 0) - Number(b.joined_at || 0));
    return participants.map(person => person.session_id);
  }, [context.participants, options.filter, options.sort]);
}
export function useParticipantIds(options: ParticipantOptions = {}) {
  const native = useNativeCallTransport();
  const useIds = native ? useNativeParticipantIds : DailyReact.useParticipantIds;
  return useIds(options);
}
function useNativeParticipantProperty<K extends keyof CallParticipant>(id: string, property: K): CallParticipant[K] | undefined {
  const context = useContext(NativeTransportContext)!;
  return context.participants[id]?.[property];
}
export function useParticipantProperty<K extends keyof CallParticipant>(id: string, property: K): CallParticipant[K] | undefined {
  const native = useNativeCallTransport();
  const useProperty = native ? useNativeParticipantProperty<K> : DailyReact.useParticipantProperty;
  // These are top-level metadata keys shared by Daily and the bridge (no track paths).
  return useProperty(id, property) as CallParticipant[K] | undefined;
}
function useNativePermissions() {
  const context = useContext(NativeTransportContext)!;
  const local = context.participants[context.localSessionId];
  const send = local?.permissions?.canSend;
  const admin = local?.permissions?.canAdmin;
  return {
    canSendAudio: send === true || (Array.isArray(send) && send.includes('audio')),
    canSendVideo: send === true || (Array.isArray(send) && send.includes('video')),
    canAdminParticipants: Boolean(local?.owner || admin === true || (Array.isArray(admin) && admin.includes('participants'))),
  };
}
export function usePermissions() {
  const native = useNativeCallTransport();
  const usePermission = native ? useNativePermissions : DailyReact.usePermissions;
  return usePermission();
}
function useNativeTranscription() { return { isTranscribing: useContext(NativeTransportContext)!.isTranscribing }; }
export function useTranscription() {
  const native = useNativeCallTransport();
  const useState = native ? useNativeTranscription : DailyReact.useTranscription;
  return useState();
}
function useNativeWaitingParticipants() {
  // The native SDK has no lobby API. Backend-authorized waiters are rendered separately.
  return { waitingParticipants: [] as { id: string; name?: string }[], grantAccess: (_id: string) => {}, denyAccess: (_id: string) => {} };
}
export function useWaitingParticipants() {
  const native = useNativeCallTransport();
  const useWaiting = native ? useNativeWaitingParticipants : DailyReact.useWaitingParticipants;
  return useWaiting();
}
function useNativeActiveSpeakerId({ ignoreLocal = false }: { ignoreLocal?: boolean } = {}) {
  const context = useContext(NativeTransportContext)!;
  const id = context.activeSpeakerId;
  if (!id || !context.participants[id] || (ignoreLocal && context.participants[id].local)) return null;
  return id;
}
export function useActiveSpeakerId(options: { ignoreLocal?: boolean } = {}) {
  const native = useNativeCallTransport();
  const useId = native ? useNativeActiveSpeakerId : DailyReact.useActiveSpeakerId;
  return useId(options);
}
export type CallScreenShare = { session_id: string; local: boolean; screenAudio?: { state?: string } };
function useNativeScreenShare(): { screens: CallScreenShare[] } {
  const context = useContext(NativeTransportContext)!;
  const { participants } = context;
  const screens = useMemo(() => Object.values(participants).filter(person => person.screen)
    .map(person => ({ session_id: person.session_id, local: Boolean(person.local) })), [participants]);
  return { screens };
}
export function useScreenShare(): { screens: CallScreenShare[] } {
  const native = useNativeCallTransport();
  const useScreens = native ? useNativeScreenShare : DailyReact.useScreenShare;
  return useScreens() as { screens: CallScreenShare[] };
}
/** Daily's native score (0-100) mapped onto the browser SDK's three thresholds. */
export const nativeNetworkThreshold = (quality?: number): NetworkThreshold =>
  quality === undefined || quality >= 70 ? 'good' : quality >= 40 ? 'low' : 'very-low';
function useNativeNetwork() { return { threshold: nativeNetworkThreshold(useContext(NativeTransportContext)!.networkQuality) }; }
export function useNetworkThreshold(): { threshold: NetworkThreshold } {
  const native = useNativeCallTransport();
  const useState = native ? useNativeNetwork : DailyReact.useNetwork;
  return { threshold: (useState().threshold || 'good') as NetworkThreshold };
}
function useNativeRecording() { return { isRecording: Boolean(useContext(NativeTransportContext)!.isRecording) }; }
export function useRecording() {
  const native = useNativeCallTransport();
  const useState = native ? useNativeRecording : DailyReact.useRecording;
  return { isRecording: Boolean(useState().isRecording) };
}
// Native snapshots carry no audio levels; tiles fall back to the active-speaker signal.
function useNativeAudioLevelObserver(_id: string, _onLevel: (level: number) => void) {}
export function useAudioLevelObserver(id: string, onLevel: (level: number) => void) {
  const native = useNativeCallTransport();
  const useObserver = native ? useNativeAudioLevelObserver : DailyReact.useAudioLevelObserver;
  useObserver(id, onLevel);
}
export type CallVideoTrackState = { state?: string; offByUser?: boolean };
function useNativeVideoTrackState(id: string, type: 'video' | 'screenVideo'): CallVideoTrackState {
  const person = useContext(NativeTransportContext)!.participants[id];
  const on = type === 'video' ? person?.video : person?.screen;
  return { state: on ? 'playable' : 'off', offByUser: !on };
}
function useDailyVideoTrackState(id: string, type: 'video' | 'screenVideo'): CallVideoTrackState {
  const state = DailyReact.useParticipantProperty(id, `tracks.${type}.state` as 'tracks.video.state') as string | undefined;
  const off = DailyReact.useParticipantProperty(id, `tracks.${type}.off` as 'tracks.video.off') as { byUser?: boolean } | undefined;
  return { state, offByUser: off?.byUser };
}
/** `tracks.<type>.state` and whether the sender turned it off, on either transport. */
export function useVideoTrackState(id: string, type: 'video' | 'screenVideo' = 'video') {
  const native = useNativeCallTransport();
  const useState = native ? useNativeVideoTrackState : useDailyVideoTrackState;
  return useState(id, type);
}
type CallVideoProps = { sessionId: string; type: 'video' | 'screenVideo'; fit: 'cover' | 'contain'; automirror?: boolean; className?: string };
/** `DailyVideo` on the web; a native video placeholder in the mobile shell. */
export const CallVideo = forwardRef<HTMLVideoElement, CallVideoProps>(function CallVideo({ sessionId, type, fit, automirror, className }, ref) {
  const placeholder = useContext(NativeVideoPlaceholderContext);
  if (useNativeCallTransport()) return <>{placeholder?.({ sessionId, type, fit, className })}</>;
  const props: ComponentProps<typeof DailyReact.DailyVideo> = { sessionId, type, fit, automirror, playsInline: true, className };
  return <DailyReact.DailyVideo ref={ref} {...props} />;
});
